/**
 * authStore.ts — estado global de autenticação (Zustand)
 *
 * Centraliza:
 * - usuário logado
 * - estado de carregamento inicial (hydration)
 * - ações de login / registro / logout
 *
 * Sessão:
 * - O accessToken vive no sessionStorage da aba e dura 15 minutos.
 * - A sessão de verdade é o refresh token, de 30 dias: cookie HttpOnly no browser,
 *   Preferences no app nativo. Por isso `hydrate` tenta retomar a sessão mesmo sem
 *   accessToken — é o que dispensa o login diário.
 * - Em caso de 401, o http.ts renova e repete a request; só quando a API recusa a
 *   renovação a sessão é encerrada e o AuthGuard manda para o login.
 */

import { create } from "zustand"
import type { AvatarUploadResponse, MeResponse, UserDto } from "@/types/auth"
import * as authService from "@/services/authService"
import { clearWorkspaces } from "@/services/workspaceService"
import type { LoginPayload, RegisterPayload, UpdateProfilePayload, ChangePasswordPayload } from "@/types/auth"
import { friendlyMessage } from "@/types/api"
import { token } from "@/lib/tokenStore"

type AuthState = {
  user: UserDto | null
  hydrated: boolean   // true quando a checagem inicial de sessão terminou
  loading: boolean
  error: string | null

  // ações
  login: (payload: LoginPayload) => Promise<void>
  register: (payload: RegisterPayload) => Promise<void>
  loginWithSso: () => Promise<void>
  registerWithSso: (fullName: string) => Promise<void>
  logout: () => Promise<void>
  hydrate: () => Promise<void>
  clearError: () => void
  updateProfile: (payload: UpdateProfilePayload) => Promise<void>
  changePassword: (payload: ChangePasswordPayload) => Promise<void>
  uploadAvatar: (file: File) => Promise<AvatarUploadResponse>
}

/** Converte tanto o DTO resumido do login quanto o perfil completo de /me. */
function normalizeUser(source: UserDto | MeResponse): UserDto {
  const raw = source as UserDto & {
    firstName?: string | null
    lastName?: string | null
    name?: string | null
    displayName?: string | null
    avatarUrl?: string | null
    profileImageUrl?: string | null
  }
  const profile = "profile" in source ? source.profile : null
  const composedName = [raw.firstName, raw.lastName].filter(Boolean).join(" ").trim()
  const fullName = composedName || profile?.fullName?.trim() || raw.fullName?.trim() || raw.name?.trim() || ""
  const nameDisplay = raw.nameDisplay?.trim() || raw.displayName?.trim()

  return {
    ...source,
    fullName,
    nameDisplay: nameDisplay && nameDisplay.toLowerCase() !== source.email?.toLowerCase()
      ? nameDisplay
      : fullName || null,
    photoUrl: raw.photoUrl || raw.avatarUrl || raw.profileImageUrl || profile?.avatarUrl || null,
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  hydrated: false,
  loading: false,
  error: null,

  clearError: () => set({ error: null }),

  hydrate: async () => {
    // Restaura o access token desta aba e descarta credenciais de versões antigas.
    const restoredToken = await token.restore()
    const savedUser = await token.restoreUser<UserDto>()

    // Exibe o cache imediatamente, mas sempre revalida /me. Assim nome, avatar
    // e demais alterações feitas em outro dispositivo não ficam obsoletos.
    if (savedUser) set({ user: normalizeUser(savedUser) })

    let knownUser: UserDto | null = savedUser

    if (!restoredToken) {
      // Aba nova ou app reaberto: o access token não sobrevive, o refresh token sim.
      const outcome = await authService.resumeSession()

      if (outcome.status !== "renewed") {
        // `rejected` significa sessão encerrada; `unavailable` (API fora do ar) também
        // cai aqui, mas sem apagar o indício de sessão — a próxima abertura tenta de novo.
        clearWorkspaces()
        set({ hydrated: true, user: null })
        return
      }

      knownUser = normalizeUser(outcome.user)
      set({ user: knownUser })
    }

    try {
      const me = await authService.getMeProfile()
      const normalized = normalizeUser(me)
      token.saveUser(normalized)
      set({ hydrated: true, user: normalized })
    } catch {
      // Uma falha de rede não deve apagar uma sessão válida já restaurada.
      if (knownUser) {
        token.saveUser(knownUser)
        set({ hydrated: true, user: knownUser })
      } else {
        token.clear()
        set({ hydrated: true, user: null })
      }
    }
  },

  login: async (payload) => {
    set({ loading: true, error: null })
    try {
      const data = await authService.login(payload)
      const user = normalizeUser(data.user)
      token.saveUser(user)
      set({ user, loading: false })
    } catch (err) {
      set({ error: friendlyMessage(err, "Erro ao fazer login."), loading: false })
      throw err
    }
  },

  register: async (payload) => {
    set({ loading: true, error: null })
    try {
      const data = await authService.register(payload)
      const user = normalizeUser(data.user)
      token.saveUser(user)
      set({ user, loading: false })
    } catch (err) {
      set({ error: friendlyMessage(err, "Erro ao cadastrar."), loading: false })
      throw err
    }
  },

  loginWithSso: async () => {
    set({ loading: true, error: null })
    try {
      const data = await authService.ssoSession()
      const user = normalizeUser(data.user)
      token.saveUser(user)
      set({ user, loading: false })
    } catch (err) {
      set({ error: friendlyMessage(err, "Erro ao autenticar com SSO."), loading: false })
      throw err
    }
  },

  registerWithSso: async (fullName) => {
    set({ loading: true, error: null })
    try {
      const data = await authService.registerWithSso(fullName)
      // A conta nasce sem senha; o /me só confirma isso na próxima hidratação.
      const user = normalizeUser({ ...data.user, hasPassword: false })
      token.saveUser(user)
      set({ user, loading: false })
    } catch (err) {
      set({ error: friendlyMessage(err, "Erro ao criar conta com Microsoft."), loading: false })
      throw err
    }
  },

  logout: async () => {
    const { flushActiveCaptures } = await import("@/components/forms/renderer/SubmissionMediaContext")
    const { flushDraftWrites } = await import("@/services/draftService")
    await flushActiveCaptures()
    await flushDraftWrites()
    // Dispara a revogação no servidor antes de limpar a tela, mas sem esperar por ela:
    // a saída tem de ser imediata para o usuário. O authService encerra a sessão local
    // de qualquer forma, inclusive se a chamada falhar.
    const pending = authService.logout()

    clearWorkspaces()
    set({ user: null, error: null })

    await pending
  },

  updateProfile: async (payload) => {
    set({ loading: true, error: null })
    try {
      const updated = await authService.updateProfile(payload)
      const normalized = normalizeUser(updated)
      const user = { ...normalized, photoUrl: normalized.photoUrl ?? useAuthStore.getState().user?.photoUrl ?? null }
      token.saveUser(user)
      set({ user, loading: false })
    } catch (err) {
      set({ error: friendlyMessage(err, "Erro ao atualizar perfil."), loading: false })
      throw err
    }
  },

  changePassword: async (payload) => {
    set({ loading: true, error: null })
    try {
      await authService.changePassword(payload)
      const current = get().user
      if (current) {
        // Uma conta SSO passa a ter senha: a tela deixa de tratá-la como "sem senha".
        const user = { ...current, hasPassword: true }
        token.saveUser(user)
        set({ user, loading: false })
      } else {
        set({ loading: false })
      }
    } catch (err) {
      set({ error: friendlyMessage(err, "Erro ao alterar senha."), loading: false })
      throw err
    }
  },

  uploadAvatar: async (file) => {
    set({ loading: true, error: null })
    try {
      const uploaded = await authService.uploadAvatar(file)
      // O backend já persiste o avatar nesse POST — refletimos a nova URL no
      // usuário em memória (e no cache) para a foto trocar na hora, sem depender
      // de um /me ou de um "Salvar" posterior.
      set((state) => {
        if (!state.user) return { loading: false }
        const user = { ...state.user, photoUrl: uploaded.photoUrl ?? state.user.photoUrl }
        token.saveUser(user)
        return { user, loading: false }
      })
      return uploaded
    } catch (err) {
      set({ error: friendlyMessage(err, "Erro ao enviar foto."), loading: false })
      throw err
    }
  },
}))
